import { Badge, Button, CloseButton, Flex, Group, Menu, Progress, TextInput, Tooltip } from "@mantine/core";
import {
  ICheckCategoryItem,
  ICheckItem,
  IChecklist,
  IChecklistCategory,
  IChecklistItem,
  IChecklistItemPopulated,
  IChecklistItemRow,
  IChecklistPopulated,
} from "@mctypes/checklist.types";
import { IMinUserDepricated, IProjectDeprecated, Paths } from "@mctypes/global.types";
import { IRowMoveInfo } from "@mcapps/shared/schemas/checklistSchema";
import {
  ColDef,
  EditableCallbackParams,
  IFilterParams,
  IRowNode,
  NewValueParams,
  RowDragEndEvent,
  RowDragEnterEvent,
  SideBarDef,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import * as R from "ramda";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { AG_GRID_LOCALE_NOR } from "../../../agGridLocale";
import { Checkbox, ConfirmDialog, CreateNewButton, ExcelExportDropdown } from "../../../components";
import { LinkifiedText } from "../../../components/LinkifiedText";
import { SimpleTagsMantine } from "../../../components/SimpleTags/SimpleTagsMantine";
import { McMantineUserSelect } from "../../../components/McMantineUserSelect/McMantineUserSelect";
import { CellEditFormatter } from "../../../components/columnGlobal";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { ToastMessagesEnums } from "@mcapps/shared/sharedConstants";
import { STANDARD_COLORS } from "../../globalConstants";
import { selectIsTemplateProject } from "../../projectSelectors";
import { sortArrayByNumber } from "../../utils";
import { formatShortDate } from "../admin/selectors";
import ChecklistModalLegacy from "./ChecklistModal/ChecklistModalLegacy";
import { useToggleCategoryInactiveMutation, useUpdateChecklistIdsMutation } from "./checklistRTK";
import { selectLatestChecklistChangeLabel } from "./checklistSelectors";
import {
  addChecklistCategoryThunk,
  addChecklistItemThunk,
  deleteChecklistCategoryThunk,
  deleteChecklistItemThunk,
  setChecklistCategoriesInRedux,
  setChecklistItemsInRedux,
  setCurrentChecklistInRedux,
  tempCollapseAllCategoriesThunk,
  toggleAllCategoryCollapseInRedux,
  toggleCategoryCollapseInRedux,
  toggleColVisibility,
  updateChecklistCategoryThunk,
  updateChecklistItemThunk,
  updateChecklistProgressInRedux,
  updateChecklistRowPositionThunk,
  updateChecklistThunk,
} from "./checklistSlice";
import { ChecklistSummaryTable } from "./ChecklistSummaryTable";
import "./ChecklistTable.css";
import { getCategoryCount, getChecklistTableRows } from "./checklistUtils";
import ExportChecklistButton from "./ExportTemplateButton";
import InitialsAndDate from "./InitialsAndDate";
import { thunkGenerateChecklistReportDocxAndDownload } from "./redux/checklistThunks";
import StampCheck from "./StampCheck";
import { IconPlus, IconSortAscendingNumbers } from "@tabler/icons-react";
import { Checkbox as MantineCheckbox, Divider, Stack, Text } from "@mantine/core";

// Types for check column filter state
interface ICheckColumnFilterModel {
  selectedPeople: string[];
  selectedStamps: string[];
}

interface ICheckColumnFiltersState {
  [columnKey: string]: ICheckColumnFilterModel;
}

// Global filter state store (shared between filter components and external filter)
let checkColumnFiltersGlobal: ICheckColumnFiltersState = {};
let onFilterChangeCallback: (() => void) | null = null;

const setCheckColumnFilter = (columnKey: string, model: ICheckColumnFilterModel) => {
  checkColumnFiltersGlobal = {
    ...checkColumnFiltersGlobal,
    [columnKey]: model,
  };
  onFilterChangeCallback?.();
};

const getCheckColumnFilter = (columnKey: string): ICheckColumnFilterModel | null => {
  return checkColumnFiltersGlobal[columnKey] ?? null;
};

const clearAllCheckColumnFilters = () => {
  checkColumnFiltersGlobal = {};
};

const ChecklistBoardAgGridDepricated: React.FC<{
  checklist: IChecklistPopulated;
  checklistCategories: IChecklistCategory[];
  checklistItems: IChecklistItem[];
  hiddenColumns: string[];
}> = ({ checklist, checklistCategories, checklistItems, hiddenColumns }) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedChecklistRow, setSelectedChecklistRow] = useState<IChecklistItemRow>(null);
  const [filterText, setFilterText] = useState<string>("");
  const [showStats, setShowStats] = useState(false);
  const gridRef = useRef<AgGridReact<IChecklistItemRow>>(null);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const allCollapsed = useMemo(
    () => checklistCategories.some((cat) => cat.collapsed === true) === true,
    [checklistCategories],
  );
  const project = useAppSelector((state) => state.adminReducer.project);
  const progress = useAppSelector((state) => state.checklistReducer.progress);
  const progressSections = useMemo(() => {
    const accepted = Math.max(0, Math.min(100, Number(progress?.accepted ?? 0)));
    const rejected = Math.max(0, Math.min(100 - accepted, Number(progress?.rejected ?? 0)));

    return {
      accepted,
      rejected,
    };
  }, [progress?.accepted, progress?.rejected]);
  const latestChecklistChange = useAppSelector(selectLatestChecklistChangeLabel);

  // Build usersById lookup for filter logic
  const usersById = useMemo(() => {
    const projectUsers = project?.members.map((m) => m.user) ?? [];
    const typedUsers = projectUsers.filter(
      (user): user is IMinUserDepricated => typeof user !== "string" && user != null,
    );
    return typedUsers.reduce((acc, user) => {
      acc[user._id] = user.name;
      return acc;
    }, {} as Record<string, string>);
  }, [project?.members]);

  // Set up external filter change listener
  useEffect(() => {
    onFilterChangeCallback = () => {
      gridRef.current?.api?.onFilterChanged();
    };
    return () => {
      onFilterChangeCallback = null;
      clearAllCheckColumnFilters();
    };
  }, []);

  // External filter: check if any check column filter is active
  const isExternalFilterPresent = useCallback(() => {
    const filters = checkColumnFiltersGlobal;
    for (const key of Object.keys(filters)) {
      const filter = filters[key];
      if (filter.selectedPeople.length > 0 || filter.selectedStamps.length > 0) {
        return true;
      }
    }
    return false;
  }, []);

  // External filter: determine if row passes all active check column filters
  const doesExternalFilterPass = useCallback(
    (node: IRowNode<IChecklistItemRow>) => {
      const row = node.data;
      if (row == null) return true;

      const filters = checkColumnFiltersGlobal;
      const isCategoryRow = row.originalItem == null;

      for (const columnKey of Object.keys(filters)) {
        const filter = filters[columnKey];
        const hasPeopleFilter = filter.selectedPeople.length > 0;
        const hasStampFilter = filter.selectedStamps.length > 0;

        if (!hasPeopleFilter && !hasStampFilter) continue;

        // Get the responsible person for this row's category
        const responsibleName = getAssignedResponsibleName(row, columnKey, usersById);

        // Person filter logic: applies to both category and item rows
        if (hasPeopleFilter) {
          if (!filter.selectedPeople.includes(responsibleName)) {
            return false; // Category (and all its children) filtered out
          }
        }

        // Stamp filter logic: only applies to item rows, category rows always pass
        if (hasStampFilter && !isCategoryRow) {
          const stampValue = mapCheckStatusToStampFilterValue(
            R.path(["originalItem", "checks", columnKey, "status"], row) as string,
          );
          if (!filter.selectedStamps.includes(stampValue)) {
            return false;
          }
        }
      }

      return true;
    },
    [usersById],
  );

  const [tableRows, setTableRows] = useState<IChecklistItemRow[]>([]);
  useEffect(() => {
    const rows = getChecklistTableRows(editMode, checklist, checklistCategories, checklistItems);
    setTableRows(rows);
  }, [editMode, checklist, checklistCategories, checklistItems]);

  useEffect(() => {
    const indexOfId = history.location.search.indexOf("id");
    if (indexOfId !== -1 && tableRows != null) {
      const indexOf = history.location.search.indexOf("=");
      const id = history.location.search.substring(indexOf + 1);
      const row = tableRows.find((x) => x._id === id);
      if (row != null) {
        setSelectedRowAndAddIdToUrl(row);
      }
    }
  }, [history.location.search.indexOf("id") !== -1, tableRows]);

  function updateRowParameter<P extends Paths<IChecklistItemPopulated, 4>>(
    id: string,
    isItem: boolean,
    path: P,
    oldValue: string,
    newValue: IChecklistItemRow[keyof IChecklistItemRow],
  ) {
    if (isItem === true) {
      dispatch(updateChecklistItemThunk(id, path, newValue, oldValue));
    } else {
      const p = path.replace(/^category\./, "");
      dispatch(updateChecklistCategoryThunk(id, p, newValue, oldValue));
    }
  }
  const [toggleCategoryInactive] = useToggleCategoryInactiveMutation();

  const setCategoryInactive = (categoryId: string, isChecked: boolean) => {
    toggleCategoryInactive({
      projectId: project._id,
      checklistId: checklist._id,
      categoryId,
      inactive: isChecked,
    });
  };

  const expandAllColumnHeader = ({ api }) => {
    return (
      <div className="w-100 d-flex justify-content-center">
        <div
          className={`expandIcon${allCollapsed === false ? " rotateIcon" : ""}`}
          onClick={() => {
            dispatch(toggleAllCategoryCollapseInRedux(!allCollapsed));
          }}
        >
          <div className="d-flex flex-column">
            <i className="fa fa-angle-down" style={{ marginBottom: "-5px" }}></i>
            <i className="fa fa-angle-down" style={{ marginTop: "-4px" }}></i>
          </div>
        </div>
      </div>
    );
  };

  const setSelectedRowAndAddIdToUrl = (row: IChecklistItemRow) => {
    setSelectedChecklistRow(row);
    history.push(`${history.location.pathname}?id=${row._id}`);
  };

  const handleToggleColumnVisibility = (e) => {
    if (e.source === "toolPanelUi") {
      const toggledColumnIds = e.columns.map((col) => col.colId);
      dispatch(toggleColVisibility({ keys: toggledColumnIds, bool: e.visible }));
    }
  };

  const excelExportColumns = (allColumns: boolean) => {
    const fileName = `${checklist.id}_${checklist.name}.xlsx`;
    const columns = allColumns ? gridRef.current?.api.getColumns() : gridRef.current?.api.getAllDisplayedColumns();
    return gridRef.current!.api.exportDataAsExcel({
      fileName,
      sheetName: fileName,
      columnKeys: columns.map((col) => col.getId()).slice(allColumns ? 5 : 1),
    });
  };

  const checkColumnsWithComment = useMemo(() => {
    const checkColumns = getCheckColumns(
      project,
      editMode,
      checklist,
      checklistCategories,
      hiddenColumns,
      updateRowParameter,
      (categoryId, param, newValue, oldValue) =>
        dispatch(updateChecklistCategoryThunk(categoryId, param, newValue, oldValue)),
    );
    const commentColumn: ColDef<IChecklistItemRow> = {
      field: "comment",
      filter: "agTextColumnFilter",
      autoHeight: true,
      headerName: `Rapportkommentar`,
      cellClass: "p-1 three-line-text-truncate",
      minWidth: 200,
      flex: 1,
      hide: editMode === true || hiddenColumns.includes("comment"),
      editable: (params) => {
        return params.data.originalItem != null;
      },
      cellEditorParams: {
        maxLength: 2000,
      },
      cellEditor: "agLargeTextCellEditor",
      cellEditorPopup: true,
      valueSetter: (params) => {
        const row = params.data as IChecklistItemRow;
        const oldValue = (row.comment ?? "") as string;
        const newValue = (params.newValue ?? "") as string;

        if (oldValue === newValue) {
          return false; // no change
        }

        updateRowParameter(row._id, true, "comment", oldValue, newValue);
        row.comment = newValue; // update row so grid shows new value
        return true;
      },
      cellRenderer: (params) => {
        const row = params.data as IChecklistItemRow;
        if (row.inactive == null || row.originalItem == null) {
          return <></>;
        }

        const value = (params.value as string) ?? "";

        return (
          <Tooltip
            openDelay={200}
            p="sm"
            withArrow
            label={value}
            multiline={true}
            w={240}
            disabled={!value || value.length <= 100}
          >
            <div className="three-line-text-truncate">{value}</div>
          </Tooltip>
        );
      },
    };
    const visibleColumnsCount = checkColumns.filter((column) => !column.hide).length;
    if (visibleColumnsCount > 3) {
      checkColumns.unshift(commentColumn);
    } else {
      checkColumns.push(commentColumn);
    }
    return checkColumns;
  }, [project?.members, editMode, checklistCategories, hiddenColumns]);

  const columns: ColDef<IChecklistItemRow>[] = useMemo(() => {
    const _cols: ColDef<IChecklistItemRow>[] = [
      {
        suppressColumnsToolPanel: true,
        pinned: "left",
        suppressMovable: true,
        headerName: "",
        maxWidth: 60,
        headerComponent: expandAllColumnHeader,
        headerClass: "p-0",
        cellClass: "justify-content-center px-0",
        cellRenderer: (params) => {
          const row = params.data as IChecklistItemRow;
          return (
            <>
              {row.originalItem == null ? (
                <div
                  onClick={() => dispatch(toggleCategoryCollapseInRedux(row._id))}
                  className={`expandIcon${row.collapsed === true ? " rotateIcon" : ""}`}
                >
                  <i className="fa fa-angle-up"></i>
                </div>
              ) : (
                <></>
              )}
            </>
          );
        },
      },
      {
        hide: editMode === false,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
        headerName: "",
        width: 100,
        resizable: false,
        cellRenderer: (params) => {
          const row = params.data as IChecklistItemRow;
          return (
            <>
              {row.originalItem != null ? (
                <ConfirmDialog
                  confirmCb={() => dispatch(deleteChecklistItemThunk(row._id))}
                  title="Slett rad"
                  message="Er du sikker på at du vil slette denne raden?"
                >
                  <Button className="mr-2" size="compact-sm" variant="light" color="red">
                    <i className="fa fa-trash fa-fw" />
                  </Button>
                </ConfirmDialog>
              ) : (
                <div className="d-flex">
                  <ConfirmDialog
                    confirmCb={() => dispatch(deleteChecklistCategoryThunk(row._id))}
                    title="Slett kategori"
                    message="Er du sikker på at du vil slette denne kategorien og alle tilhørende rader?"
                  >
                    <Button className="mr-2" size="compact-sm" variant="light" color="red">
                      <i className="fa fa-trash fa-fw" />
                    </Button>
                  </ConfirmDialog>
                  <Button size="compact-sm" variant="light" color="green" onClick={() => addItem(row.originalCategory)}>
                    <IconPlus stroke={4} size={16} />
                  </Button>
                </div>
              )}
            </>
          );
        },
      },
      {
        field: "inactive.isChecked",
        headerName: "IR",
        hide: editMode === true,
        width: 65,
        filter: true,
        filterParams: {
          suppressMiniFilter: true,
        },
        valueGetter: (params) => (params.data.inactive?.isChecked ? "Ikke relevant" : "Relevant"),
        cellRenderer: (params) => {
          const row = params.data as IChecklistItemRow;
          const showInactiveTooltip = row.inactive.isChecked === true && row.originalItem != null;

          return (
            <Tooltip
              withArrow
              position="bottom"
              openDelay={200}
              disabled={!showInactiveTooltip}
              label={
                <>
                  {`${(row.inactive.updatedBy as IMinUserDepricated)?.name || ""}`}
                  <br />
                  {formatShortDate((row.inactive.updatedDate || "").toString())}
                </>
              }
            >
              <div className="d-flex w-100 align-items-center justify-content-center">
                <Checkbox
                  icon="ban"
                  isChecked={row.inactive.isChecked === true}
                  setChecked={() => {
                    if (row.originalItem == null) {
                      setCategoryInactive(row._id, !row.inactive.isChecked);
                      return;
                    }
                    updateRowParameter(
                      row._id,
                      true,
                      "inactive.isChecked",
                      row.inactive?.isChecked.toString(),
                      !row.inactive?.isChecked,
                    );
                  }}
                  disabled={false}
                  color="var(--mantine-color-mc-primary-6)"
                />
              </div>
            </Tooltip>
          );
        },
      },
      {
        field: "rowId",
        filter: "agTextColumnFilter",
        headerName: "Nr.",
        rowDrag: editMode === true,
        hide: editMode === false && hiddenColumns.includes("rowId"),
        width: 100,
        minWidth: 80,
        cellClass: "px-0",
        cellRenderer: (params) => {
          const row = params.data as IChecklistItemRow;
          return (
            <div className="d-flex p-0 m-0 align-items-center">
              <Button
                variant="subtle"
                color="mc-primary"
                px={"xs"}
                size="sm"
                fw={600}
                onClick={() => setSelectedRowAndAddIdToUrl(row)}
              >
                {row.inactive != null && row.originalItem?.checks == null ? (
                  <b className={`${row.inactive?.isChecked === true ? "text-inactive" : ""}`}>{row.rowId}</b>
                ) : (
                  <span className={`${row.inactive?.isChecked === true ? "text-inactive" : ""}`}>{row.rowId}</span>
                )}
              </Button>
              <div className="flex-fill" />
            </div>
          );
        },
      },
      {
        field: "title",
        filter: "agTextColumnFilter",
        headerName: "Kontrollspørsmål (krav)",
        hide: editMode === false && hiddenColumns.includes("title"),
        flex: 2,
        maxWidth: 800,
        editable: editMode,
        autoHeight: true,
        cellClass: (params) => {
          const row = params.data as IChecklistItemRow;
          return row?.originalItem == null ? "grid-display-block bold-cell" : "grid-display-block";
        },
        minWidth: 380,
        cellEditor: editMode === true ? "agLargeTextCellEditor" : undefined,
        cellEditorPopup: editMode,
        cellEditorParams: {
          maxLength: 5000,
        },
        cellRenderer: (params) => <LinkifiedText text={params.value} />,
        cellStyle: { whiteSpace: "break-spaces" },
        onCellValueChanged:
          editMode === true
            ? (event: NewValueParams<IChecklistItemRow, string>) => {
                const isItem = event.data.originalItem != null;
                const oldValue = (isItem ? event.data.originalItem?.title : event.data.originalCategory?.title) ?? "-";
                const newValue = event.newValue ?? "-";

                updateRowParameter(event.data._id, isItem, "title", oldValue, newValue);
              }
            : undefined,
      },
      {
        field: "reference",
        editable: (params: EditableCallbackParams<IChecklistItemRow, string>) => {
          return params.data.originalItem != null && editMode === true;
        },
        hide: editMode === false && hiddenColumns.includes("reference"),
        filter: "agTextColumnFilter",
        headerName: "Referanse",
        autoHeight: true,
        cellEditor: editMode === true ? "agLargeTextCellEditor" : undefined,
        cellEditorPopup: editMode,
        cellEditorParams: {
          maxLength: 5000,
        },
        onCellValueChanged:
          editMode === true
            ? (event: NewValueParams<IChecklistItemRow, string>) => {
                const isItem = event.data.originalItem != null;
                const oldValue = event.data.originalItem.reference ?? "";
                const newValue = event.newValue ?? "";

                if (isItem && event.data.originalItem.reference !== event.newValue) {
                  updateRowParameter(event.data._id, isItem, "reference", oldValue, newValue);
                }
              }
            : undefined,

        cellRenderer: (params) => CellEditFormatter(params.value, "reference"),
      },
      {
        field: "dueDate",
        hide: editMode === true || hiddenColumns.includes("dueDate"),
        editable: (params: EditableCallbackParams<IChecklistItemRow, string>) => {
          return params.data.originalItem == null;
        },
        cellEditorPopup: false,
        cellEditor: "agDateStringCellEditor",
        valueGetter: (params) => {
          const date = params.data.dueDate != null ? params.data.dueDate.split("T")[0] : null;
          return date;
        },
        valueSetter: (params) => {
          updateRowParameter(params.data._id, false, "category.dueDate", params.oldValue, params.newValue);
          return true;
        },
        filter: "agDateColumnFilter",
        headerName: "Frist",
        cellClass: "px-1 grid-display-flex ",
        cellStyle: {
          display: "inline-block",
        },
        minWidth: 130,
        width: 130,
      },
      ...checkColumnsWithComment,
    ];

    if (checklist.itemTagsActive === true) {
      _cols.push({
        field: "itemTags",
        headerName: "Etiketter",
        hide: checklist.itemTagsActive !== true,
        cellClass: "grid-display-block p-0",
        maxWidth: 200,
        autoHeight: true,
        cellEditorPopup: true,
        cellRenderer: (params) => {
          const row = params.data as IChecklistItemRow;
          return row.originalItem != null ? (
            <div className="d-flex align-items-center flex-wrap h-100">
              <CellTags
                allTags={checklist.itemTags ?? []}
                currentTags={row.originalItem.itemTags ?? []}
                editMode={editMode}
                onChange={(tags) =>
                  updateRowParameter(
                    row._id,
                    row.originalItem != null,
                    "itemTags",
                    JSON.stringify(row.originalItem.itemTags),
                    tags,
                  )
                }
              />
            </div>
          ) : (
            <></>
          );
        },
      });
    }

    return _cols;
  }, [editMode, allCollapsed, tableRows, hiddenColumns]);

  const sideBar = useMemo(() => {
    const sb: SideBarDef = {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivotMode: true,
          },
        },
      ],
      position: "left",
    };
    return sb;
  }, []);

  useEffect(() => {
    if (checklist.isTemplate === true) {
      setEditMode(true);
    }
  }, []);

  useEffect(() => {
    if (selectedChecklistRow != null) {
      setSelectedChecklistRow(tableRows.find((x) => x._id === selectedChecklistRow._id));
    }
  }, [tableRows]);

  const addCategory = async () => {
    dispatch(addChecklistCategoryThunk(getCategoryCount(checklistCategories)));
  };

  const addItem = async (category: IChecklistCategory) => {
    dispatch(addChecklistItemThunk(category._id));
  };

  const closeModal = async () => {
    setSelectedChecklistRow(null);
    history.push(history.location.pathname);
  };

  const [updateChecklistIds, { data: checklistParts, isError: updateIdError }] = useUpdateChecklistIdsMutation();

  useEffect(() => {
    if (updateIdError) {
      dispatch(setShowSuccessMessageThunk("Feil ved tilbakestilling av ID-sekvenser"));
      return;
    }

    if (checklistParts == null) {
      return;
    }

    dispatch(setShowSuccessMessageThunk("ID-sekvenser tilbakestilt"));

    const { categories, checklist, items } = checklistParts;

    dispatch(updateChecklistProgressInRedux(checklist.progress));
    dispatch(setCurrentChecklistInRedux(checklist));
    dispatch(setChecklistItemsInRedux(sortArrayByNumber(items, "itemId")));
    dispatch(setChecklistCategoriesInRedux(sortArrayByNumber(categories, "categoryId")));
  }, [checklistParts]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      resizable: true,
      suppressHeaderMenuButton: true,
      editable: false,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
        wordBreak: "break-word",
      },
    }),
    [],
  );

  const onRowDragEnter = (event: RowDragEnterEvent<IChecklistItemRow>) => {
    if (event.node.data.originalItem === null) {
      dispatch(tempCollapseAllCategoriesThunk(true));
    }
  };

  const findNewCategoryIndex = (newPosition: number): number => {
    let foundIndex = -1;

    const displayRow = gridRef.current.api.getDisplayedRowAtIndex(newPosition - 1);
    const prevRowCategoryId = displayRow?.data?.categoryId ?? -1;

    gridRef.current.api.forEachNode((node) => {
      const data = node.data;

      if (data.originalItem === null && data.categoryId === prevRowCategoryId) {
        foundIndex = node.rowIndex;
        return;
      }
    });

    return foundIndex;
  };

  const onRowDragEnd = async (event: RowDragEndEvent<IChecklistItemRow>) => {
    const draggedRow = event.node.data;
    const isCat = draggedRow.originalItem == null;
    const isItem = draggedRow.originalItem != null;
    const newPosition = event.node.rowIndex;

    // reset grid data to "cancel" move if the dragged row is an item and is moved to the top of the grid
    if (isItem === true && newPosition === 0) {
      gridRef.current.api.setGridOption("rowData", filteredData);
      dispatch(tempCollapseAllCategoriesThunk(false));
      return;
    }

    const newCatIndex = findNewCategoryIndex(newPosition);
    const newCat = gridRef.current.api.getDisplayedRowAtIndex(newCatIndex)?.data;
    const newRelativePosition = newPosition - newCatIndex;
    const newIndex = event.node.rowIndex;
    const originalIndex = filteredData.findIndex((i) => i._id === draggedRow._id);

    if (originalIndex === event.node.rowIndex || event.overNode?.data == null) {
      dispatch(tempCollapseAllCategoriesThunk(false));
      return;
    }

    const requestInfo: IRowMoveInfo = {
      isCat,
      oldCategoryId: draggedRow.originalCategory._id,
      newCategoryId: newCat?.originalCategory._id ?? null,
      rowId: isCat ? draggedRow.originalCategory._id : draggedRow.originalItem._id,
      position: isCat ? newIndex : newRelativePosition - 1,
      checklistId: checklist._id,
    };

    if (isCat === true) {
      dispatch(tempCollapseAllCategoriesThunk(false));
    }

    await dispatch(updateChecklistRowPositionThunk(requestInfo));
  };

  const filteredData = useMemo(() => {
    return tableRows.filter((row) => row.originalItem == null || (row.originalItem != null && row.collapsed !== true));
  }, [tableRows]);

  const updateChecklist = async (prop: string, oldVal, newVal) => {
    await dispatch(updateChecklistThunk(prop, oldVal, newVal));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.SETTINGS_UPDATED));
  };

  const isTemplateProject = useAppSelector(selectIsTemplateProject);

  return (
    <>
      <div className="d-flex m-2">
        <h3 className="d-flex align-items-center">
          <div className="mx-2">{checklist.name}</div>
        </h3>
        {editMode === false && (
          <Flex justify="flex-end" align="center" style={{ flexGrow: 1 }}>
            <div className="w-100" style={{ maxWidth: "40%", minWidth: 260 }}>
              {latestChecklistChange != null && (
                <Text size="xs" c="dimmed" ta="right" mb={3}>
                  Sist endret: {latestChecklistChange}
                </Text>
              )}
              <Progress.Root className="w-100" h="16px" radius="4px">
                <Progress.Section value={progressSections.accepted} color={STANDARD_COLORS.green} />
                <Progress.Section value={progressSections.rejected} color={STANDARD_COLORS.red} />
              </Progress.Root>
            </div>
          </Flex>
        )}
      </div>

      <Flex align="center" gap="xs" wrap="wrap" className="mx-2 mb-2">
        {editMode === true && (
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Tilbakestill ID-sekvenser" withArrow openDelay={300}>
              <Button
                color="mc-primary"
                size="sm"
                variant="outline"
                onClick={() => updateChecklistIds({ projectId: project._id, checklistId: checklist._id })}
                leftSection={<IconSortAscendingNumbers size={16} />}
              >
                ID
              </Button>
            </Tooltip>
            <CreateNewButton onClick={addCategory} size="sm" tooltip="Legg til kategori" />
          </Group>
        )}

        <TextInput
          size="sm"
          placeholder="Søk…"
          value={filterText}
          onChange={(e) => setFilterText(e.currentTarget.value)}
          w={170}
          rightSection={filterText.length > 0 && <CloseButton onClick={() => setFilterText("")} size="sm" />}
        />

        <Group align="center" gap="xs" wrap="nowrap">
          <TagToggle
            tags={checklist.itemTags}
            currentTags={checklist?.activeItemTags}
            onChange={async (currTags) => {
              try {
                await updateChecklist("activeItemTags", checklist?.activeItemTags, currTags);
              } catch (error) {
                // dispatch(setShowFailedMessage("Etiketter ej oppdatert"));
              }
            }}
          />
          <InitialsAndDate
            updatedBy={checklist.activeItemTagChangedBy}
            updatedDate={checklist.activeItemTagChangedDate}
            singleLine={true}
          />
        </Group>

        {editMode === false && (
          <Group gap="xs" wrap="nowrap" style={{ marginLeft: "auto" }}>
            <Button color="lightgray.9" size="sm-compact" variant="outline" onClick={() => setShowStats(!showStats)}>
              <i className="fa fa-bar-chart fa-fw" />
              Vis statistikk
            </Button>
            <Button
              color="lightgray.9"
              size="sm-compact"
              variant="outline"
              onClick={() => {
                dispatch(thunkGenerateChecklistReportDocxAndDownload());
              }}
            >
              <i className="fa fa-file-word-o fa-fw mr-1" />
              Generer utskrift
            </Button>
            <ExcelExportDropdown
              buttonVariant="outline"
              excelExportVisibleColumns={() => excelExportColumns(false)}
              exportExcelAllColumns={() => excelExportColumns(true)}
            />
            <Button
              variant="filled"
              color="mc-primary"
              size="sm-compact"
              onClick={() => {
                dispatch(toggleAllCategoryCollapseInRedux(false));
                setEditMode(true);
              }}
            >
              <i className="fa fa-pencil fa-fw mr-1" />
              Rediger sjekkliste
            </Button>
          </Group>
        )}

        {editMode === true && (
          <Group gap="xs" wrap="nowrap" style={{ marginLeft: "auto" }}>
            {isTemplateProject && <ExportChecklistButton checklistLocalId={checklist.id} />}
            {checklist.isTemplate === false && (
              <Button color="green" size="sm-compact" variant="filled" onClick={() => setEditMode(false)}>
                <i className="fa fa-check fa-fw mr-1" />
                Lukk redigering
              </Button>
            )}
          </Group>
        )}
      </Flex>

      {tableRows != null && columns.length !== 0 && (
        <>
          {showStats && (
            <div className="bg-light p-3">
              <ChecklistSummaryTable checklist={checklist} items={checklistItems} />
            </div>
          )}
          <div className="d-flex flex-column border h-100 overflow-auto">
            <div className="ag-theme-quartz h-100">
              <AgGridReact
                onColumnVisible={handleToggleColumnVisibility}
                enableCellTextSelection={editMode === false}
                rowHeight={53}
                maintainColumnOrder={true}
                ref={gridRef}
                getRowId={(p) => p.data._id}
                rowData={filteredData}
                columnDefs={columns}
                stopEditingWhenCellsLoseFocus={true}
                defaultColDef={defaultColDef}
                animateRows={true}
                rowBuffer={50}
                localeText={AG_GRID_LOCALE_NOR}
                quickFilterText={filterText}
                sideBar={editMode !== true ? sideBar : null}
                suppressMoveWhenRowDragging={true}
                suppressCutToClipboard={true}
                rowDragManaged={true}
                onRowDragEnter={onRowDragEnter}
                onRowDragEnd={onRowDragEnd}
                suppressColumnVirtualisation={true}
                isExternalFilterPresent={isExternalFilterPresent}
                doesExternalFilterPass={doesExternalFilterPass}
              />
            </div>

            {selectedChecklistRow != null && (
              <ChecklistModalLegacy
                stampSettings={checklist.stampSettings}
                editable={editMode === true}
                checklistRow={selectedChecklistRow}
                closeIssueItemModal={closeModal}
                updateRowAttribute={(rowId, path, oldValue, newValue) =>
                  updateRowParameter(rowId, true, path, oldValue, newValue as any)
                }
                columns={columns}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default ChecklistBoardAgGridDepricated;

const CHECKLIST_STAMP_FILTER_VALUES = ["Ja", "Nei", "-", "IR", "UAV", "Deaktivert"] as const;
type TChecklistStampFilterValue = (typeof CHECKLIST_STAMP_FILTER_VALUES)[number];

const mapCheckStatusToStampFilterValue = (status: string): TChecklistStampFilterValue => {
  switch (status) {
    case "YES":
      return "Ja";
    case "NO":
      return "Nei";
    case "IR":
      return "IR";
    case "UAV":
      return "UAV";
    case "DISABLED":
      return "Deaktivert";
    default:
      return "-";
  }
};

const CHECK_COLUMN_MIN_WIDTH = 180;
const CHECK_COLUMN_MAX_WIDTH = 360;

const getResponsibleColumnWidth = (
  categories: IChecklistCategory[],
  checklistKey: string,
  usersById: Record<string, string>,
): number => {
  const longestNameLength = (categories ?? []).reduce((longest, category) => {
    const assignedTo = R.path(["checks", checklistKey, "assignedTo"], category) as string | IMinUserDepricated;
    const responsibleName = getResponsibleNameFromValue(assignedTo, usersById);

    return Math.max(longest, responsibleName.length);
  }, 0);

  return Math.min(CHECK_COLUMN_MAX_WIDTH, Math.max(CHECK_COLUMN_MIN_WIDTH, longestNameLength * 8 + 88));
};

const getResponsibleNameFromValue = (
  assignedTo: string | IMinUserDepricated,
  usersById: Record<string, string>,
): string => {
  if (assignedTo == null) {
    return "Ingen ansvarlig";
  }

  if (typeof assignedTo === "string") {
    return usersById[assignedTo] ?? "Ingen ansvarlig";
  }

  return assignedTo.name ?? "Ingen ansvarlig";
};

const getAssignedResponsibleName = (
  row: IChecklistItemRow,
  checklistKey: string,
  usersById: Record<string, string>,
): string => {
  if (row == null) {
    return "Ingen ansvarlig";
  }

  const assignedTo = R.path(["originalCategory", "checks", checklistKey, "assignedTo"], row) as
    | string
    | IMinUserDepricated;

  return getResponsibleNameFromValue(assignedTo, usersById);
};

const getStampFilterValueForRow = (
  row: IChecklistItemRow,
  checklistKey: string,
): TChecklistStampFilterValue | TChecklistStampFilterValue[] => {
  if (row?.originalItem == null) {
    return [...CHECKLIST_STAMP_FILTER_VALUES];
  }

  const status = R.path(["originalItem", "checks", checklistKey, "status"], row) as string;
  return mapCheckStatusToStampFilterValue(status);
};

// Custom filter component for check columns with People and Stamp sections
interface IChecklistCheckFilterParams extends IFilterParams {
  checklistKey: string;
  usersById: Record<string, string>;
}

const ChecklistCheckColumnFilter = forwardRef((props: IChecklistCheckFilterParams, ref) => {
  const { checklistKey, usersById, api } = props;

  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
  const [selectedStamps, setSelectedStamps] = useState<string[]>([]);
  const [availablePeople, setAvailablePeople] = useState<string[]>([]);
  const [availableStamps, setAvailableStamps] = useState<string[]>([]);

  // Compute available values from grid data
  const computeAvailableValues = useCallback(() => {
    const peopleSet = new Set<string>();
    const stampsSet = new Set<string>();

    api?.forEachNode((node) => {
      const row = node.data as IChecklistItemRow;
      if (row == null) return;

      // Get person from category assignment
      const personName = getAssignedResponsibleName(row, checklistKey, usersById);
      peopleSet.add(personName);

      // Get stamp value for item rows only
      if (row.originalItem != null) {
        const status = R.path(["originalItem", "checks", checklistKey, "status"], row) as string;
        if (status) {
          stampsSet.add(mapCheckStatusToStampFilterValue(status));
        }
      }
    });

    setAvailablePeople(
      Array.from(peopleSet).sort((a, b) => {
        // Put "Ingen ansvarlig" last
        if (a === "Ingen ansvarlig") return 1;
        if (b === "Ingen ansvarlig") return -1;
        return a.localeCompare(b);
      }),
    );

    // Sort stamps in a logical order
    const stampOrder = ["Ja", "Nei", "-", "IR", "UAV", "Deaktivert"];
    setAvailableStamps(Array.from(stampsSet).sort((a, b) => stampOrder.indexOf(a) - stampOrder.indexOf(b)));
  }, [api, checklistKey, usersById]);

  // Compute values on mount and when grid data changes
  useEffect(() => {
    computeAvailableValues();
  }, [computeAvailableValues]);

  // Initialize from global state if exists
  useEffect(() => {
    const existing = getCheckColumnFilter(checklistKey);
    if (existing) {
      setSelectedPeople(existing.selectedPeople);
      setSelectedStamps(existing.selectedStamps);
    }
  }, [checklistKey]);

  const updateFilter = useCallback(
    (people: string[], stamps: string[]) => {
      setSelectedPeople(people);
      setSelectedStamps(stamps);
      setCheckColumnFilter(checklistKey, {
        selectedPeople: people,
        selectedStamps: stamps,
      });
    },
    [checklistKey],
  );

  const togglePerson = useCallback(
    (person: string) => {
      const newPeople = selectedPeople.includes(person)
        ? selectedPeople.filter((p) => p !== person)
        : [...selectedPeople, person];
      updateFilter(newPeople, selectedStamps);
    },
    [selectedPeople, selectedStamps, updateFilter],
  );

  const toggleStamp = useCallback(
    (stamp: string) => {
      const newStamps = selectedStamps.includes(stamp)
        ? selectedStamps.filter((s) => s !== stamp)
        : [...selectedStamps, stamp];
      updateFilter(selectedPeople, newStamps);
    },
    [selectedPeople, selectedStamps, updateFilter],
  );

  const clearAll = useCallback(() => {
    updateFilter([], []);
  }, [updateFilter]);

  const selectAllPeople = useCallback(() => {
    updateFilter([...availablePeople], selectedStamps);
  }, [availablePeople, selectedStamps, updateFilter]);

  const selectAllStamps = useCallback(() => {
    updateFilter(selectedPeople, [...availableStamps]);
  }, [selectedPeople, availableStamps, updateFilter]);

  // AG Grid filter interface
  useImperativeHandle(ref, () => ({
    isFilterActive: () => selectedPeople.length > 0 || selectedStamps.length > 0,
    getModel: () => {
      if (selectedPeople.length === 0 && selectedStamps.length === 0) {
        return null;
      }
      return { selectedPeople, selectedStamps };
    },
    setModel: (model: ICheckColumnFilterModel | null) => {
      if (model == null) {
        updateFilter([], []);
      } else {
        updateFilter(model.selectedPeople ?? [], model.selectedStamps ?? []);
      }
    },
    doesFilterPass: () => true, // Handled by external filter
    getModelAsString: () => {
      const parts: string[] = [];
      if (selectedPeople.length > 0) {
        parts.push(`Ansvarlig: ${selectedPeople.join(", ")}`);
      }
      if (selectedStamps.length > 0) {
        parts.push(`Stempel: ${selectedStamps.join(", ")}`);
      }
      return parts.join(" | ");
    },
  }));

  const isActive = selectedPeople.length > 0 || selectedStamps.length > 0;

  return (
    <div style={{ padding: "12px", minWidth: "220px", maxHeight: "400px", overflowY: "auto" }}>
      <Stack gap="xs">
        {/* People section */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text fw={600} size="sm">
              Ansvarlig
            </Text>
            <Button
              variant="subtle"
              size="compact-xs"
              onClick={
                selectedPeople.length === availablePeople.length
                  ? () => updateFilter([], selectedStamps)
                  : selectAllPeople
              }
            >
              {selectedPeople.length === availablePeople.length ? "Fjern alle" : "Velg alle"}
            </Button>
          </Group>
          <Stack gap={4}>
            {availablePeople.map((person) => (
              <MantineCheckbox
                key={person}
                label={person}
                size="sm"
                checked={selectedPeople.includes(person)}
                onChange={() => togglePerson(person)}
              />
            ))}
          </Stack>
        </div>

        <Divider />

        {/* Stamp section */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text fw={600} size="sm">
              Stempel
            </Text>
            <Button
              variant="subtle"
              size="compact-xs"
              onClick={
                selectedStamps.length === availableStamps.length
                  ? () => updateFilter(selectedPeople, [])
                  : selectAllStamps
              }
            >
              {selectedStamps.length === availableStamps.length ? "Fjern alle" : "Velg alle"}
            </Button>
          </Group>
          <Stack gap={4}>
            {availableStamps.map((stamp) => (
              <MantineCheckbox
                key={stamp}
                label={stamp}
                size="sm"
                checked={selectedStamps.includes(stamp)}
                onChange={() => toggleStamp(stamp)}
              />
            ))}
          </Stack>
        </div>

        {isActive && (
          <>
            <Divider />
            <Button variant="light" color="gray" size="compact-sm" onClick={clearAll}>
              Nullstill filter
            </Button>
          </>
        )}
      </Stack>
    </div>
  );
});

ChecklistCheckColumnFilter.displayName = "ChecklistCheckColumnFilter";

const getCheckColumns = (
  project: IProjectDeprecated,
  editMode: boolean,
  checklist: IChecklist,
  checklistCategories: IChecklistCategory[],
  hiddenColumns: string[],
  updateRowParameter: (id: string, boolean, status: string, oldValue, newValue) => void,
  updateChecklistCategory: (categoryId: string, param: string, newValue: string, oldValue: string) => void,
): ColDef<any>[] => {
  const projectUsers = project?.members.map((m) => m.user);
  const typedProjectUsers = (projectUsers ?? []).filter(
    (user): user is IMinUserDepricated => typeof user !== "string" && user != null,
  );
  const usersComboboxData = typedProjectUsers.map((user) => ({
    value: user._id,
    label: user.name,
  }));
  const usersById = typedProjectUsers.reduce((acc, user) => {
    acc[user._id] = user.name;
    return acc;
  }, {} as Record<string, string>);

  const nrOfCheckColumns = 15;

  const columns = [];
  for (let i = 1; i <= nrOfCheckColumns; i++) {
    const checklistKey = `check${i}`;
    const isActive = R.path(["checkTypes", checklistKey, "active"], checklist);
    const dynamicWidth = getResponsibleColumnWidth(checklistCategories, checklistKey, usersById);
    columns.push({
      field: `checks.check${i}.status`,
      headerName: R.path(["checkTypes", checklistKey, "name"], checklist),
      hide:
        hiddenColumns.includes(`checks.check${i}.status`) ||
        isActive !== true ||
        (editMode === true && checklist.includeIrOnDefaultRoles !== true),
      suppressColumnsToolPanel: isActive !== true,
      cellClass: "grid-display-block",
      cellStyle: {
        overflow: "visible",
        padding: "0px 4px",
        display: "inline-block",
      },
      minWidth: dynamicWidth,
      width: dynamicWidth,
      filter: ChecklistCheckColumnFilter,
      filterParams: {
        checklistKey,
        usersById,
      },
      valueGetter: (params) => {
        const row = params.data as IChecklistItemRow;
        if (row?.originalItem != null) {
          const status = R.path(["originalItem", "checks", checklistKey, "status"], row) as string;
          return mapCheckStatusToStampFilterValue(status);
        }

        return getAssignedResponsibleName(row, checklistKey, usersById);
      },
      cellRenderer: (params) => {
        const row = params.data as IChecklistItemRow;
        const statusDisabled = (row.checks?.[checklistKey] as ICheckItem)?.status === "DISABLED";

        return (
          <>
            {row.inactive != null && row.originalItem?.checks ? (
              <div className="d-flex align-items-center h-100 grid-cell-center-fix">
                <StampCheck
                  templateMode={checklist.isTemplate}
                  includeIr={i > 3 || (checklist.isTemplate === true && statusDisabled !== true)}
                  includeDisabled={checklist.isTemplate === true}
                  statusDisabled={statusDisabled}
                  disabled={
                    row.inactive.isChecked === true || (checklist.isTemplate !== true && statusDisabled === true)
                  }
                  toggle={(id, oldValue, newValue) =>
                    updateRowParameter(id, true, `checks.check${i}.status`, oldValue, newValue)
                  }
                  updateCommentText={(text) =>
                    updateRowParameter(row._id, true, `checks.check${i}.comment.text`, "", text)
                  }
                  checkItem={R.path(["originalItem", "checks", checklistKey], row)}
                  row={row}
                  stampSettings={checklist.stampSettings}
                  includeIrOnDefaultRoles={checklist.includeIrOnDefaultRoles === true && statusDisabled !== true}
                  includeUavOnDefaultRoles={checklist.includeUavOnDefaultRoles === true && statusDisabled !== true}
                />
              </div>
            ) : (
              <div style={{ marginTop: "2px" }} className=" grid-cell-center-fix">
                {checklist.isTemplate !== true && (
                  <McMantineUserSelect
                    disabled={checklist.doksam?.docId != null}
                    withinPortal
                    variant="unstyled"
                    data={usersComboboxData}
                    value={
                      typeof R.path(["originalCategory", "checks", `check${i}`, "assignedTo"], row) === "string"
                        ? (R.path(["originalCategory", "checks", `check${i}`, "assignedTo"], row) as string)
                        : (R.path(["originalCategory", "checks", `check${i}`, "assignedTo"], row) as IMinUserDepricated)
                            ?._id ?? null
                    }
                    onChange={(value) =>
                      updateChecklistCategory(
                        row._id,
                        `checks.check${i}.assignedTo`,
                        value,
                        R.path(["originalCategory", "checks", `check${i}`, "assignedTo"], row),
                      )
                    }
                  />
                )}
              </div>
            )}
          </>
        );
      },
    });
  }
  return columns;
};

const CellTags: React.FC<{
  allTags: string[];
  currentTags: string[];
  editMode: boolean;
  onChange: (currentTags: string[]) => void;
}> = ({ allTags, currentTags, editMode, onChange }) => {
  const menuItems = allTags.filter((tag) => !currentTags.includes(tag));
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <Flex align="center" gap={6} wrap="wrap">
      {editMode === true && (
        <Menu opened={menuOpen} onChange={setMenuOpen} position="bottom-start" withinPortal>
          <Menu.Target>
            <Button color="green" variant="light" size="compact-sm" disabled={allTags.length === currentTags.length}>
              <IconPlus stroke={4} size={16} />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {menuItems.map((tag) => (
              <Menu.Item key={tag} onClick={() => onChange([...currentTags, tag])}>
                {tag}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}

      <SimpleTagsMantine
        activeTags={[...currentTags].sort()}
        tagOptions={allTags}
        editable={editMode}
        allowNew={false}
        showAddButton={false}
        tagSize="sm"
        tagColor="lightgray.6"
        showTagIcon={false}
        onChange={onChange}
      />
    </Flex>
  );
};

const TagToggle: React.FC<{ tags: string[]; currentTags: string[]; onChange: (tags: string[]) => void }> = ({
  tags = [],
  currentTags = [],
  onChange,
}) => {
  return (
    <Group gap={"xs"} wrap="nowrap">
      {[...tags].sort().map((tag) => {
        const isActive = currentTags.includes(tag);

        return (
          <Badge
            className={`checklist-tag-toggle-badge ${
              isActive ? "checklist-tag-toggle-badge--active" : "checklist-tag-toggle-badge--inactive"
            }`}
            key={tag}
            color={isActive ? "mc-primary" : "lightgray.6"}
            variant={isActive ? "filled" : "outline"}
            onClick={() => onChange(isActive ? currentTags.filter((e) => e !== tag) : [...currentTags, tag])}
          >
            {tag}
          </Badge>
        );
      })}
    </Group>
  );
};
